import { FEATURE_FLAGS } from "@solomon-apps/config";
import { Button } from "@solomon-labs/design/components/ui";
import React from "react";

interface PositionsActionRowProps {
    onSwapUsdv: () => void;
    squads?: boolean;
    /** Below the minimum balance, topping up is the only action worth
     *  offering, so it replaces Swap rather than sitting beside it. */
    belowMinimum?: boolean;
    onBuyUsdv?: () => void;
    /** Claim mode puts an earned Claim button in the hero opposite this
     *  row. Two raised CTAs on one screen compete, so Swap steps back to
     *  sunken + outline and leaves Claim as the only raised surface.
     *  Stream mode has no Claim button, so Swap stays raised. */
    deEmphasizeSwap?: boolean;
    /** Drives whether the Saved Destinations entry-point shows. The
     *  mode itself is switched from the footer link stack, not here —
     *  a toggle in this row silently rewired the Update button two rows
     *  above it with no indication the two were connected. */
    advancedMode: boolean;
    /** Mode-3 — opens the Reward Destinations panel. Only the link is
     *  shown; the parent owns the panel mount. */
    onOpenSetDestinations: () => void;
}

/**
 * The position's action, left, + optional Saved Destinations link,
 * right. Shared between the PositionsSection render path and the real
 * Program Position render path so the action surface stays identical
 * whether the user reloads into a position or just enrolled.
 *
 * Below the minimum the left slot becomes "Buy More USDv" instead of
 * "Swap USDv". It used to render inside YaasPositionCard, which put it
 * ABOVE the destination rows while every other state put its action
 * below them. One slot, one order, whatever the state.
 */
export function PositionsActionRow({
    onSwapUsdv,
    squads = false,
    belowMinimum = false,
    onBuyUsdv,
    deEmphasizeSwap = false,
    advancedMode,
    onOpenSetDestinations
}: PositionsActionRowProps): React.ReactElement {
    return (
        <div className="flex flex-col-reverse md:flex-row items-stretch md:items-start md:justify-between gap-3 mt-6">
            {belowMinimum && onBuyUsdv ? (
                <Button
                    variant="raised"
                    interaction="lifted"
                    size="lg"
                    fullWidth
                    className="md:w-auto"
                    onClick={onBuyUsdv}
                >
                    {squads ? "Open in Squads" : "Buy More USDv"}
                </Button>
            ) : (
                <Button
                    variant={deEmphasizeSwap ? "sunken" : "raised"}
                    outline={deEmphasizeSwap}
                    interaction="lifted"
                    size="lg"
                    fullWidth
                    className="md:w-auto"
                    onClick={onSwapUsdv}
                >
                    {squads ? "Open in Squads" : "Swap USDv"}
                </Button>
            )}
            {/* Saved Destinations is only meaningful once routing can
                span more than one destination — hence both the master
                routing gate and the multi-destination gate. The
                `advancedMode` check stays on top of them: the flags say
                the mode is *available*, this says the user is in it. */}
            {FEATURE_FLAGS.retailPositionRouting &&
                FEATURE_FLAGS.retailMultiDestinationRouting &&
                advancedMode && (
                    <div className="flex flex-col items-end gap-1">
                        <Button
                            variant="link"
                            size="sm"
                            onClick={onOpenSetDestinations}
                        >
                            Saved Destinations
                        </Button>
                    </div>
                )}
        </div>
    );
}
